/**
 * client/src/features/income/components/IncomePage.jsx
 * 
 * Main container component for the Income Page.
 * Organizes the layout of the income management interface.
 */
import React, { useState, useEffect, useCallback } from 'react';
import IncomeOverview from './IncomeOverview';
import BucketList from './BucketList/BucketList';
import useIncome from '../hooks/useIncome';
import useBuckets from '../hooks/useBuckets';
import { PlusIcon } from '@heroicons/react/24/outline';
import { calculateBucketIncome } from '../utils/incomeUtils';

/**
 * Income Page Component
 * 
 * Main container for the income management feature.
 * Displays income overview and bucket list sections.
 */
const IncomePage = ({ isPartnerMode = false, isOurMode = false, partnerName = '' }) => {
  // State for tracking loading and errors
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for manual refresh trigger
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Get income and bucket data using custom hooks
  // If isOurMode, fetch both user's and partner's data combined
  // If isPartnerMode, fetch only partner's data
  // Otherwise, fetch only user's own data
  const { incomes, totalMonthlyIncome, fetchIncomes } = useIncome({ 
    isPartnerMode,
    includePartner: isOurMode // When in "Our" mode, include partner's incomes
  });
  const { buckets, refreshBuckets, reorderBuckets, createBucket } = useBuckets({ 
    isPartnerMode,
    includePartner: isOurMode // When in "Our" mode, include partner's buckets
  });

  // Enhanced refresh function that ensures both incomes and buckets are updated
  const refreshAllData = useCallback(async () => {
    try {
      console.log('🔄 Refreshing all income and bucket data...');
      
      // Refresh both incomes and buckets in parallel
      await Promise.all([
        fetchIncomes(),
        refreshBuckets()
      ]);
      
      console.log('✅ All data refreshed successfully');
    } catch (err) {
      console.error('❌ Error refreshing data:', err);
      throw err;
    }
  }, [fetchIncomes, refreshBuckets]);

  // Load data on component mount or when refresh is triggered
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        console.log('🔄 Loading income page data...');

        // Load incomes and buckets in parallel
        await refreshAllData();
        
        setLoading(false);
        console.log('✅ Income page data loaded successfully');
      } catch (err) {
        console.error('❌ Error loading income page data:', err);
        setError('Failed to load data. Please refresh the page and try again.');
        setLoading(false);
      }
    };

    loadData();
  }, [refreshAllData, refreshTrigger]);

  // Handle bucket reordering
  const handleReorderBuckets = async (updatedBuckets) => {
    try {
      console.log('🔄 Reordering buckets...');
      await reorderBuckets(updatedBuckets);
      console.log('✅ Buckets reordered successfully');
    } catch (err) {
      console.error('❌ Error reordering buckets:', err);
      setError('Failed to reorder buckets. Please try again.');
    }
  };

  // Force a refresh of data
  const forceRefresh = useCallback(() => {
    console.log('🔄 Forcing data refresh...');
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Enhanced refresh function for incomes that also updates the overview
  const refreshIncomesWithOverview = useCallback(async () => {
    try {
      console.log('🔄 Refreshing incomes with overview update...');
      await refreshAllData();
      console.log('✅ Incomes and overview refreshed successfully');
    } catch (err) {
      console.error('❌ Error refreshing incomes:', err);
      // Don't throw error here to prevent disrupting the UI
    }
  }, [refreshAllData]);

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-pulse flex flex-col items-center">
          <div className="rounded-full bg-gradient-to-r from-violet-300 to-indigo-300 h-12 w-12 mb-4"></div>
          <div className="h-4 w-32 bg-gradient-to-r from-violet-200 to-indigo-200 rounded"></div>
          <p className="mt-4 text-gray-500">Loading income data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen text-center p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 max-w-md">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h3 className="text-xl font-semibold text-red-700 mb-2">Something went wrong</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Calculate totals
  const calculateTotals = () => {
    // Initialize totals
    let monthlyGross = 0;
    let monthlyNet = 0;
    let monthlyTax = 0;

    // Ensure buckets is an array
    const bucketsArray = Array.isArray(buckets) ? buckets : [];

    // Calculate totals by summing up each bucket's totals
    bucketsArray.forEach(bucket => {
      // Calculate this bucket's totals
      const bucketTotals = calculateBucketIncome(incomes, bucket._id);
      
      // Add to overall totals
      monthlyGross += bucketTotals.gross;
      monthlyNet += bucketTotals.net;
      monthlyTax += bucketTotals.tax;
    });

    return {
      monthlyGross,
      monthlyNet,
      monthlyTax
    };
  };

  const totals = calculateTotals();

  // Before the return statement, add console logs for debugging
  console.log('IncomePage render - incomes:', incomes);
  console.log('IncomePage render - buckets:', buckets);

  // Determine page title based on mode
  const getPageTitle = () => {
    if (isPartnerMode) return `${partnerName}'s Income`;
    if (isOurMode) return 'Our Combined Income';
    return 'Income';
  };
  
  const getPageDescription = () => {
    if (isPartnerMode) return `View ${partnerName}'s income sources by bucket`;
    if (isOurMode) return 'View your combined income sources by bucket';
    return 'Manage your income sources by bucket';
  };

  return (
    <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4 md:py-6 max-w-screen-xl">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 sm:mb-4 md:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
            {getPageTitle()}
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            {getPageDescription()}
          </p>
        </div>
      </div>
      
      {/* Income Overview Section */}
      <div className="mb-4 sm:mb-6 md:mb-8">
        <IncomeOverview 
          incomesArray={incomes} 
          buckets={buckets}
          key={`${incomes.length}-${buckets.length}-${Date.now()}`}
        />
      </div>
      
      {/* Bucket List Section */}
      <div>
        <BucketList
          buckets={buckets}
          incomes={incomes}
          onReorderBuckets={handleReorderBuckets}
          refreshIncomes={refreshIncomesWithOverview}
          isPartnerMode={isPartnerMode}
        />
      </div>
    </div>
  );
};

export default IncomePage; 